<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate :disabled="disabled" @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-pages'"
              :title="data.title ? `${data.title}` : `Создать страницу`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in $options.tabs" :key="tab.value">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-card-text>
                <PageContent>
                  <v-container>
                    <InputTitleSlug
                      :is-loading="loading || disabled"
                      :title="fields.title"
                      :title-error="validationErrors.title"
                      :slug="fields.slug"
                      :isSlugEnabled="!isNew"
                      :slug-error="validationErrors.slug"
                      @change:title="fields.title = $event"
                      @change:slug="fields.slug = $event"
                      @input:slug="$v.fields.slug.$touch"
                    />
                    <v-row>
                      <v-col cols="12">
                        <span>Подзаголовок (<TranslationIcon />)</span>
                        <v-text-field
                          outlined
                          :disabled="disabled"
                          :loading="loading"
                          v-model="fields.subtitle"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Обложка'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.coverImageId"
                          @change="fields.coverImageId = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <MediaFilePicker
                          :label="'Миниатюра'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.thumbnailId"
                          @change="fields.thumbnailId = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <span class="mr-5">Домашняя страница</span>
                        <v-switch
                          inset
                          dense
                          :disabled="disabled"
                          :loading="loading"
                          v-model="fields.isHome"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <span>Место проведения</span>
                        <LocationAutocomplete
                          :address="fields.address"
                          :loading="loading || disabled"
                          @set-location="setLocation"
                          @update-location-part="updateLocation"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <SeoSettings
                          :data="data"
                          :loading="loading || disabled"
                          @on-input-seo="fields = Object.assign({}, fields, $event)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </PageContent>
              </v-card-text>
            </v-tab-item>
            <v-tab-item>
              <v-card-text>
                <PageContent>
                  <DynamicListWrapper
                    :list="fields.widgets"
                    :error-messages="mixValidationErrors"
                    :available-items="$options.availableWidgets"
                    :copy-clipboard="mixCopyListItemClipboard"
                    :options="$options.options"
                    @list-sort="mixListItemsReassort"
                    @add="mixCreateListItemHandler"
                    @option="mixListItemOptionHandler"
                  >
                    <template #item="{ data }">
                      <component
                        :is="data.item.type"
                        :list-item="data.item"
                        :error-messages="mixDynamicListValidationErrors[data.itemIndex]"
                        @change="mixListItemChangeHandler($event, data.itemIndex)"
                        @change-item="
                          mixListItemChangeItemHandler($event, data.itemIndex)
                        "
                        @remove-item="
                          mixListItemRemoveItemHandler($event, data.itemIndex)
                        "
                      />
                    </template>
                  </DynamicListWrapper>
                </PageContent>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :published-at="data.publishedAt"
          :author="data.author"
          :is-loading="loading"
          :is-submit-disabled="disabled"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'page'"
          :sortOrder="fields.sortOrder"
          @submit="submit"
          @delete="$emit('delete-page')"
          @cancel="$router.push({ name: 'page-pages' })"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row v-if="!isNew" align="center">
              <v-col cols="6">
                <span>Посмотреть на сайте</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  target="_blank"
                  :href="redirerectToWebPortal"
                  :loading="loading"
                  :disabled="disabled"
                  ><v-icon>mdi-web</v-icon></v-btn
                >
              </v-col>
            </v-row>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import validationRules from './validation-rules.js'
import { commonWidgetList } from '@/utils/widgets'
import tabs from '@/configs/tabs.js'
import LocationAutocomplete from '@/components/location-autocomplete'
import Geolocation from '@/utils/types/Geolocation.js'
import widgetParentMix from '@/mixins/widgets/widgetParent.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import parentDynamicList from '@/mixins/dynamic-list/parentDynamicList.mix.js'
import DynamicListWrapper from '@/components/dynamic-list-wrapper/DynamicListWrapper.vue'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import Widget from '@/utils/widgets/Widget'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import { mixForm } from '@/utils/form.mix.js'
import StatusPicker from '@/components/status-picker'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'

export default {
  extends: FormAbstract,
  mediaTypesByKeys: mediaTypesByKeys(),
  name: 'FormPage',
  tabs,
  validationRules,
  availableWidgets: commonWidgetList,
  dynamicListName: 'widgets',
  mixins: [
    widgetParentMix,
    parentDynamicList,
    validationTranslations,
    prepareTranslations,
    mixForm
  ],
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    DynamicListWrapper,
    LocationAutocomplete,
    MediaFilePicker,
    SeoSettings,
    FormHeader,
    StatusPicker,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 0,
      fields: {
        title: null,
        slug: undefined,
        isHome: false,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImageId: null,
        status: null,
        widgets: [],
        address: new Geolocation(),
        thumbnailId: null,
        subtitle: null,
        coverImageId: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      }
    }
  },
  computed: {
    redirerectToWebPortal () {
      if (this.data) {
        const { slug } = this.data
        if (slug === '/') {
          return window.location.href.includes('demo')
            ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}${slug}`
            : `${process.env.VUE_APP_WEB_PORTAL_URL}${slug}`
        } else {
          return window.location.href.includes('demo')
            ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}/${slug}`
            : `${process.env.VUE_APP_WEB_PORTAL_URL}/${slug}`
        }
      }
      return '#'
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.address = this.data?.address || new Geolocation()
      this.fields.widgets = Widget.populateList(data.widgets)
      this.$set(this.fields, 'coverImageId', data?.coverImage?.id || null)
      this.$set(this.fields, 'thumbnailId', data?.thumbnail?.id || null)
    }
  },
  methods: {
    setLocation (location) {
      this.fields.address = location
    },
    updateLocation ({ value, fieldName }) {
      this.fields.address[fieldName] = value
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      payload.address.lon = Number(payload.address.lon)
      payload.address.lat = Number(payload.address.lat)

      // TODO
      delete payload.address.__typename
      this.mixPrepareTranslations(payload)
      const isWidgetsInValid = this.mixValidateDynamicList(payload.widgets)
      if (!isWidgetsInValid) {
        this.$emit('submit', { data: payload })
      } else {
        this.$errorMsg('Не заполнены обязательные поля виджетов!')
      }
    }
  }
}
</script>
